import type { ProviderAccount } from "../../accounts/types.ts";
import type { Account, Client } from "../../clients/types.ts";
import { HttpRequestError } from "../../errors/http.ts";
import type { StacksTransaction } from "../../transactions/types.ts";
import {
	clearTxCache,
	serializeTransaction,
} from "../../transactions/wire/serialize.ts";
import type { IntegerType } from "../../utils/encoding.ts";
import { intToBigInt } from "../../utils/encoding.ts";
import { estimateFee } from "../public/estimateFee.ts";

export function isProviderAccount(
	account: Account,
): account is ProviderAccount {
	return account.type === "provider";
}

/**
 * Named fee tiers. `'low' | 'mid' | 'high'` map to the node's three fee
 * estimations; `'min'` is the node's minimum relay fee — 1 uSTX per byte of
 * the serialized transaction, computable offline.
 */
export type FeeTier = "min" | "low" | "mid" | "high";

/** Fee input accepted by wallet actions: an explicit amount or a named tier. */
export type FeeParam = IntegerType | FeeTier;

const FEE_TIERS: readonly FeeTier[] = ["min", "low", "mid", "high"];
const TIER_INDEX = { low: 0, mid: 1, high: 2 } as const;

export function isFeeTier(fee: FeeParam | undefined): fee is FeeTier {
	return typeof fee === "string" && FEE_TIERS.includes(fee as FeeTier);
}

/**
 * Minimum relay fee: 1 uSTX per byte of the serialized transaction. The fee
 * field is fixed-width (8 bytes), so the size — and therefore this floor — is
 * stable regardless of the fee value later set on the spending condition.
 */
export function minimumFee(transaction: StacksTransaction): bigint {
	return BigInt(serializeTransaction(transaction).length);
}

/** Outcome of {@link resolveFee}: the amount and which tier produced it. */
export type ResolvedFee = {
	fee: bigint;
	/**
	 * Tier the amount came from. `'min'` when the estimator had no estimate
	 * and the relay floor was used instead. Absent when the caller passed an
	 * exact amount.
	 */
	tier?: FeeTier;
};

/**
 * True when the node answered `POST /v2/fees/transaction` with 400
 * `NoEstimateAvailable`: it is healthy but has no fee history for this
 * payload (fresh devnet, quiet chain). Every other failure means the
 * estimate was never obtained and must surface to the caller.
 */
export function isNoEstimateAvailable(error: unknown): boolean {
	return (
		error instanceof HttpRequestError &&
		error.status === 400 &&
		(error.details?.includes("NoEstimateAvailable") ?? false)
	);
}

/**
 * Resolve a fee param to a concrete amount. Numeric input passes through.
 * Tiers `'low' | 'mid' | 'high'` index the node's estimations (nearest
 * available when fewer than three are returned); `'min'` resolves to
 * {@link minimumFee}, which needs no network round-trip.
 *
 * The relay floor is the fallback ONLY when the node reports
 * `NoEstimateAvailable` or returns no estimations. A transport timeout, 5xx,
 * or auth failure rethrows: silently under-paying because the node was
 * unreachable would strand the transaction in the mempool.
 */
export async function resolveFee(
	client: Client,
	transaction: StacksTransaction,
	fee: FeeParam | undefined,
): Promise<ResolvedFee> {
	if (fee !== undefined && !isFeeTier(fee)) return { fee: intToBigInt(fee) };

	const tier = fee ?? "mid";
	if (tier === "min") return { fee: minimumFee(transaction), tier };

	let estimates: Awaited<ReturnType<typeof estimateFee>>;
	try {
		estimates = await estimateFee(client, { transaction });
	} catch (error) {
		if (!isNoEstimateAvailable(error)) throw error;
		return { fee: minimumFee(transaction), tier: "min" };
	}
	const pick = estimates[TIER_INDEX[tier]] ?? estimates[estimates.length - 1];
	if (pick) return { fee: BigInt(pick.fee), tier };
	return { fee: minimumFee(transaction), tier: "min" };
}

/** Set the resolved fee on an unsigned transaction's origin spending condition. */
export function setUnsignedFee(
	transaction: StacksTransaction,
	fee: bigint,
): void {
	// biome-ignore lint/suspicious/noExplicitAny: interop boundary or dynamic-shape value where typing adds friction without runtime safety
	(transaction.auth.spendingCondition as any).fee = fee;
	// In-place mutation: invalidate the memoized serialization (resolveFee may
	// have already serialized this object via minimumFee/estimateFee).
	clearTxCache(transaction);
}

/** Throw when a named fee tier is used with a provider (wallet) account. */
export function assertNoFeeTierForProvider(fee: FeeParam | undefined): void {
	if (isFeeTier(fee)) {
		throw new Error(
			`Fee tier '${fee}' requires local signing; provider (wallet) accounts set their own fee`,
		);
	}
}
